import { v4 } from "uuid";

import {
  Clause,
  ClauseGroup,
  Junctions,
  Operators,
  RuleBranch,
  SplitBranch,
} from "src/clients/flow-api";

// The conditions of a branch: either plain clauses or groups, never mixed.
export type BranchClauses = Clause[] | ClauseGroup[];

// A rule branch whose conditions may be grouped.
export type RuleBranchWithGroups = Omit<RuleBranch, "clauses"> & {
  clauses: BranchClauses;
};

// A split branch whose conditions may be grouped.
export type SplitBranchWithGroups = Omit<SplitBranch, "clauses"> & {
  clauses: BranchClauses;
};

// Tells a group of clauses apart from a single clause.
export const isClauseGroup = (
  entry: Clause | ClauseGroup,
): entry is ClauseGroup => "clauses" in entry;

export const isGroupedBranch = (
  clauses: BranchClauses,
): clauses is ClauseGroup[] =>
  clauses.length > 0 && clauses.every(isClauseGroup);

// Returns every clause of a branch, unwrapping any groups.
export const flattenBranchClauses = (clauses: BranchClauses): Clause[] => {
  if (isGroupedBranch(clauses)) {
    return clauses.flatMap((group) => group.clauses);
  }
  return clauses;
};

// The editor always works on groups: a flat branch is wrapped into one lone
// group carrying the branch junction, so the rows read the same.
export const toGroups = (
  clauses: BranchClauses,
  junction: Junctions,
): ClauseGroup[] =>
  isGroupedBranch(clauses) ? clauses : [{ junction, clauses }];

// Creates an empty clause with fresh field ids.
export const getNewClause = (): Clause => ({
  id_left: v4(),
  id_right: v4(),
  left: "",
  right: "",
  operator: Operators.EQ,
});

// Creates a group holding one empty clause.
export const getNewClauseGroup = (): ClauseGroup => ({
  junction: Junctions.AND,
  clauses: [getNewClause()],
});
